import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const TERMS = [
  { key: "First", label: "Term-I" },
  { key: "Second", label: "Term-II" },
  { key: "Final", label: "Final Exam" },
];

const toCamelCase = (str) => {
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// Renders the full block (marks page + activities/grade page) for ONE student.
function StudentTermsMarksheet({
  records,
  school,
  activities,
  totalGrade,
  totalActivity,
  terms,
  isLast,
  themeColor,
  selectedTerm,
}) {
  // extract unique subjects for this student
  const subjects = useMemo(() => {
    const allMarks = records.flatMap((ms) => ms.marks || []);
    const unique = [
      ...new Map(allMarks.map((m) => [m.subjectName.trim(), m])).values(),
    ];
    return unique.sort((a, b) =>
      a.subjectName.toLowerCase().localeCompare(b.subjectName.toLowerCase())
    );
  }, [records]);

  const findTerm = (examType) =>
    records.find((m) =>
      m.examType?.trim().toLowerCase().includes(examType?.trim().toLowerCase())
    );

  const findMark = (examType, subject) => {
    const term = findTerm(examType);
    return term?.marks.find(
      (s) => s.subjectName.trim().toLowerCase() === subject.trim().toLowerCase()
    );
  };

  const getTotals = (examType) => {
    const term = findTerm(examType);
    if (!term) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade };
  };

  // Only the term(s) currently selected in the filter get rendered.
  const visibleTerms =
    selectedTerm === "all" ? TERMS : TERMS.filter((t) => t.key === selectedTerm);

  const totalsByTerm = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotals(t.key);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  // profile fields come from the "First" term record, falling back to the first record
  const profile = findTerm("First") || records[0];

  // result/remarks/percentage should come from the Final term record if present
  const finalRecord = findTerm("Final") || records[records.length - 1];

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);
  const cellSx = { border: `1px solid ${borderColor}`, padding: "8px" };
  const headCellSx = { ...cellSx, background: headerBg };
  const cell1 = { border: `1px double ${borderColor}`, padding: "0px 2px" };

  return (
    <>
      <Box sx={{ background: "#fff", fontFamily: "Arial" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            mb: 1,
            mt: 0,
          }}
        >
          <img
            src={school.logoUrl}
            alt="School Logo"
            style={{ width: 90, height: 80, marginTop: 1 }}
          />

          <Typography variant="h5" align="center" fontWeight="bold" sx={{ color: borderColor }}>
            {toCamelCase(school.schoolName)}
          </Typography>
          <Typography variant="subtitle2" align="center">
            {toCamelCase(
              [
                school?.addressDTO.addressLine1,
                school?.addressDTO.city,
                school?.addressDTO.pincode,
                school?.addressDTO.state,
              ].join(", ")
            )}
          </Typography>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ fontWeight: "bold", my: 1 }}
          >
            PROGRESS REPORT SESSION - {profile?.academicYear}
          </Typography>
        </Box>
        <Divider sx={{ mb: 1, mt: 1, borderColor }} />

        {/* Student Info */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>
            <strong>Student's Name: </strong>{" "}
            {toCamelCase(
              `${[profile?.name, profile?.father, profile?.last]
                .filter(Boolean)
                .join(" ")}`
            )}
          </Typography>
          <Typography>
            <strong>Standard: </strong>
            {profile?.classRoom?.standard} | {profile?.classRoom?.section}
          </Typography>
          <Typography>
            <strong>Roll No.: </strong> {profile?.classRoom?.rollNumber}
          </Typography>
        </Box>
        <Divider sx={{ m: 1, borderColor }} />

        {/* Marks Table */}
        <Box sx={{ py: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx} rowSpan={2} align="center">
                  <strong>Subject</strong>
                </TableCell>

                {visibleTerms.map((term) => (
                  <TableCell key={term.key} sx={headCellSx} colSpan={4} align="center">
                    <strong>{term.label}</strong>
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                {visibleTerms.flatMap((term) => [
                  <TableCell key={`max-${term.key}`} sx={headCellSx} align="center">
                    Max
                  </TableCell>,
                  <TableCell key={`min-${term.key}`} sx={headCellSx} align="center">
                    Min
                  </TableCell>,
                  <TableCell key={`obt-${term.key}`} sx={headCellSx} align="center">
                    Obt
                  </TableCell>,
                  <TableCell key={`grade-${term.key}`} sx={headCellSx} align="center">
                    Grade
                  </TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{toCamelCase(sub.subjectName)}</TableCell>

                  {visibleTerms.flatMap((term) => {
                    const mark = findMark(term.key, sub.subjectName);
                    return [
                      <TableCell key={`${term.key}-max-${i}`} sx={cellSx} align="center">
                        {mark?.maxMark || 100}
                      </TableCell>,
                      <TableCell key={`${term.key}-min-${i}`} sx={cellSx} align="center">
                        {(mark?.maxMark * 34) / 100 || 35}
                      </TableCell>,
                      <TableCell key={`${term.key}-obt-${i}`} sx={cellSx} align="center">
                        {mark?.marksObtained || "-"}
                      </TableCell>,
                      <TableCell key={`${term.key}-grade-${i}`} sx={cellSx} align="center">
                        {mark?.grade || "-"}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              ))}

              {/* Totals Row */}
              <TableRow sx={{ background: totalsBg }}>
                <TableCell sx={cellSx}>
                  <strong>Total Marks</strong>
                </TableCell>

                {visibleTerms.flatMap((term) => {
                  const t = totalsByTerm[term.key];
                  return [
                    <TableCell key={`${term.key}-tmax`} sx={cellSx} align="center">
                      {t.max}
                    </TableCell>,
                    <TableCell key={`${term.key}-tmin`} sx={cellSx} />,
                    <TableCell key={`${term.key}-tobt`} sx={cellSx} align="center">
                      {t.obt}
                    </TableCell>,
                    <TableCell key={`${term.key}-tgrade`} sx={cellSx} align="center">
                      {t.grade}
                    </TableCell>,
                  ];
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Result: {finalRecord?.result || "PASS"}</Typography>
          <Typography>Remarks: {finalRecord?.remarks || "Excellent Performance"}</Typography>
          <Typography>Percentage: {finalRecord?.percentage ?? 87}%</Typography>
        </Box>

        <Divider sx={{ mb: 2, borderColor }} />

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Class Teacher: ______________</Typography>
          <Typography>Head Master: ______________</Typography>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      <Box sx={{ width: "100%", mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={cell1} colSpan={activities.length + 1} align="center">
                <strong>Co-Curricular Activity</strong>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={cell1} align="center">
                {"#"}
              </TableCell>
              {activities.map((a, i) => (
                <TableCell sx={cell1} key={i} align="center">
                  {a.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {totalActivity.map((sub, i) => {
              return (
                <TableRow key={i}>
                  <TableCell sx={cell1}>{toCamelCase(sub.name)}</TableCell>
                  {activities.map((_, j) => (
                    <TableCell sx={cell1} align="center" key={j}>
                      <TextField
                        variant="standard"
                        placeholder="Very Good"
                        slotProps={{
                          input: {
                            disableUnderline: true,
                          },
                        }}
                        sx={{
                          "& input": {
                            fontFamily: "inherit",
                            fontSize: "14px",
                          },
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={cell1} colSpan={totalGrade.length + 1} align="center">
                <strong>Total Grade</strong>
              </TableCell>
            </TableRow>

            <TableRow>
              {totalGrade.map((a, j) => (
                <TableCell sx={cell1} key={j} align="center">
                  {a.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {subjects.map((sub, i) => {
              return (
                <TableRow key={i}>
                  {i < 3 &&
                    totalGrade.map((_, j) => (
                      <TableCell key={j} sx={cell1}>
                        {j === 0 ? (
                          terms[i]?.n || "-"
                        ) : (
                          <TextField
                            variant="standard"
                            placeholder=".........."
                            slotProps={{ input: { disableUnderline: true } }}
                          />
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TextField
          sx={{
            mt: 1,
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
            },
          }}
          variant="standard"
          placeholder="Very Good"
          slotProps={{
            input: { disableUnderline: true },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            my: 2,
          }}
        >
          <Typography variant="body2">Class Teacher sign </Typography>
          <Typography variant="body2">Head Master </Typography>
        </Box>
      </Box>

      {/* page break between students (not after the very last one) */}
      {!isLast && <div className="pagebreak"></div>}
    </>
  );
}

export default function TermsMarksheetReceiptBulk({ receiptData, school, setTemID }) {
  const [activities] = useState([
    { name: "Formative Assessment-I" },
    { name: "Formative Assessment-II" },
    { name: "Final Exam" },
  ]);

  const totalGrade = [
    { name: "Semester" },
    { name: "Education" },
    { name: "Co-Curricular" },
    { name: "Total" },
    { name: "Special Remark" },
    { name: "C. Teacher Signature" },
  ];

  const totalActivity = [
    { name: "Qualities" },
    { name: "Creativity" },
    { name: "Regularity" },
    { name: "Punctuality" },
    { name: "Cleanness" },
    { name: "Cultural" },
    { name: "Discipline" },
    { name: "Confidence" },
    { name: "School Program" },
    { name: "School Mates " },
  ];

  const [terms] = useState([{ n: "First" }, { n: "Second" }, { n: "Final" }]);

  // --- Customization controls (theme color + term filter) ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all"); // "all" | "First" | "Second" | "Final"

  // receiptData.data is now a FLAT array of term-records across possibly many students
  const allRecords = receiptData.data || [];
  const receiptId = `marksheet`;

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Group flat term-records into one array per student.
  // Grouping key preference: admission id -> rollNumber -> full name (fallback).
  const studentGroups = useMemo(() => {
    const groups = new Map();

    allRecords.forEach((record) => {
      const key =
        record.id ??
        record.classRoom?.rollNumber ??
        `${record.name || ""}-${record.last || ""}-${record.father || ""}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(record);
    });

    return Array.from(groups.values());
  }, [allRecords]);

  return (
    <Box id={receiptId}>
      {/* Controls: hidden when printing */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "800px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Theme Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 40,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="term-filter-label">Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {TERMS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {studentGroups.map((records, idx) => (
        <StudentTermsMarksheet
          key={records[0]?.id ?? idx}
          records={records}
          school={school}
          activities={activities}
          totalGrade={totalGrade}
          totalActivity={totalActivity}
          terms={terms}
          isLast={idx === studentGroups.length - 1}
          themeColor={themeColor}
          selectedTerm={selectedTerm}
        />
      ))}
    </Box>
  );
}
